import { PublicLayout } from '../../components/marketing/PublicLayout'
import { Link } from 'react-router-dom'

export default function TermsOfServicePage() {
  return (
    <PublicLayout>
      <div className="py-16 md:py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-deep-charcoal mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-gray-600">
              Effective Date: January 20, 2025 • Last Updated: January 20, 2025
            </p>
          </div>

          {/* Content */}
          <div className="space-y-12">
            {/* Introduction */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Agreement to Terms</h2>
              <p className="text-base text-gray-600 leading-relaxed mb-4">
                These Terms of Service ("Terms") constitute a legally binding agreement between you and TalkToast LLC ("TalkToast", "we", "us", or "our") concerning your access to and use of the TalkToast website, web application, mobile applications, and related services (collectively, the "Service").
              </p>
              <p className="text-base text-gray-600 leading-relaxed">
                <strong>By accessing or using the Service, you agree to be bound by these Terms.</strong> If you do not agree to these Terms, you may not access or use the Service.
              </p>
            </section>

            {/* Eligibility */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Eligibility</h2>
              <p className="text-base text-gray-600 leading-relaxed mb-4">
                You must be at least 18 years old to use the Service.
              </p>
              <p className="text-base text-gray-600 leading-relaxed">
                By using the Service, you represent and warrant that you are at least 18 years of age, have the legal capacity to enter into these Terms, and will comply with these Terms and all applicable laws.
              </p>
            </section>

            {/* Account Registration */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Account Registration</h2>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3 mt-6">Optional Account Creation</h3>
              <p className="text-base text-gray-600 mb-4">
                TalkToast can be used without creating an account (Free Tier). To access premium features, you may register using:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4 mb-4">
                <li>Email and password</li>
                <li>Google account (OAuth)</li>
                <li>Apple account (OAuth)</li>
                <li>Facebook account (OAuth)</li>
              </ul>

              <div className="bg-toast-gold/10 border border-toast-gold/30 rounded-lg p-6 mt-6">
                <h3 className="text-lg font-semibold text-deep-charcoal mb-2">Your Responsibilities:</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  <li>Provide accurate, current information (if creating an account)</li>
                  <li>Keep your account secure (don't share passwords)</li>
                  <li>Notify us immediately of unauthorized access</li>
                  <li>Don't create accounts using false information</li>
                </ul>
              </div>
            </section>

            {/* Subscription Tiers */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Subscription Tiers and Pricing</h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                TalkToast operates on a freemium model with three subscription tiers:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-warm-cream rounded-lg p-6 border border-soft-tan">
                  <h3 className="text-xl font-semibold text-deep-charcoal mb-2">Free Tier</h3>
                  <p className="text-2xl font-bold text-toast-gold mb-4">$0</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• No account required</li>
                    <li>• Unlimited database meal planning</li>
                    <li>• Shopping list creation</li>
                    <li>• Up to 25 favorites</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-toast-gold/20 to-transparent rounded-lg p-6 border-2 border-toast-gold">
                  <h3 className="text-xl font-semibold text-deep-charcoal mb-2">Basic Tier</h3>
                  <p className="text-2xl font-bold text-toast-gold mb-1">$49.99<span className="text-sm font-normal text-gray-600">/year</span></p>
                  <p className="text-sm text-gray-600 mb-4">or $4.99/month</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Everything in Free</li>
                    <li>• Limited AI recipe customization</li>
                    <li>• Limited re-rolls</li>
                    <li>• Calorie targeting</li>
                    <li>• Advanced cuisines (18+)</li>
                    <li>• .ics calendar export</li>
                    <li>• Up to 100 favorites</li>
                    <li>• Priority support</li>
                  </ul>
                </div>

                <div className="bg-warm-cream rounded-lg p-6 border border-soft-tan">
                  <h3 className="text-xl font-semibold text-deep-charcoal mb-2">Premium Tier</h3>
                  <p className="text-2xl font-bold text-toast-gold mb-1">$99.99<span className="text-sm font-normal text-gray-600">/year</span></p>
                  <p className="text-sm text-gray-600 mb-4">or $9.99/month</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Everything in Basic</li>
                    <li>• Unlimited AI customization</li>
                    <li>• Unlimited re-rolls</li>
                    <li>• Unlimited AI chat</li>
                    <li>• Meal prep mode</li>
                    <li>• Macro tracking</li>
                    <li>• Direct cart push</li>
                    <li>• Unlimited favorites</li>
                  </ul>
                </div>
              </div>

              <div className="bg-warm-cream rounded-lg p-4 border border-soft-tan">
                <p className="text-sm text-gray-600">
                  <strong>Free Trials:</strong> 7-day free trial for Premium tier (credit card required). Automatically downgrade to Free tier if not converted.
                </p>
              </div>
            </section>

            {/* License and Restrictions */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">License and Restrictions</h2>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3">License Grant</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Subject to your compliance with these Terms, TalkToast grants you a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Service for your personal, non-commercial use.
              </p>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3">Restrictions</h3>
              <p className="text-base text-gray-600 mb-4">
                <strong>You agree NOT to:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                <li>Copy, modify, or create derivative works of the Service</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                <li>Rent, lease, sell, or sublicense access to the Service</li>
                <li>Use the Service to build a competing product</li>
                <li>Access the Service through automated means (bots, scrapers) without permission</li>
                <li>Interfere with or disrupt the Service</li>
                <li>Use the Service for any illegal or unauthorized purpose</li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Intellectual Property</h2>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3">TalkToast Ownership</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                TalkToast and its licensors own all rights, title, and interest in the Service, including software, algorithms, designs, trademarks, and content. Your use of the Service does not grant you ownership of any intellectual property rights.
              </p>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3">Your Content</h3>
              <div className="bg-toast-gold/10 border border-toast-gold/30 rounded-lg p-6">
                <p className="text-base text-gray-700 mb-3">
                  <strong>You Retain Ownership</strong> of:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4 mb-4">
                  <li>Meal preferences and dietary restrictions</li>
                  <li>Custom recipes you upload</li>
                  <li>Notes, ratings, and feedback</li>
                  <li>Shopping lists and calendar entries</li>
                </ul>
                <p className="text-base text-gray-700">
                  By using the Service, you grant us a limited license to store, process, and display your content as necessary to provide the Service.
                </p>
              </div>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3 mt-6">AI-Generated Recipes</h3>
              <div className="bg-warm-cream rounded-lg p-6 border border-soft-tan">
                <p className="text-base text-gray-700 mb-3">
                  <strong>Ownership:</strong> TalkToast retains all ownership rights to AI-generated recipes, meal plans, and related content created by the Service.
                </p>
                <p className="text-base text-gray-600 mb-3">
                  <strong>License to You:</strong> You are granted a personal, non-exclusive, non-transferable license to use AI-generated recipes for personal, non-commercial purposes only.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-3">
                  <p className="text-base text-gray-700 font-semibold mb-2">Prohibited Uses:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                    <li>Reselling, republishing, or distributing AI-generated recipes commercially</li>
                    <li>Using recipes in a competing product or service</li>
                    <li>Creating derivative works for commercial purposes</li>
                    <li>Claiming ownership or authorship of AI-generated content</li>
                  </ul>
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  You may cook, share with friends and family, and modify recipes for personal use. Commercial use requires written permission from TalkToast.
                </p>
              </div>
            </section>

            {/* Health and Safety Disclaimer */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Health and Safety Disclaimer</h2>
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
                <p className="text-lg font-semibold text-red-800 mb-3">
                  ⚠️ IMPORTANT: TalkToast is a meal planning tool, not medical or nutritional advice.
                </p>
                <p className="text-base text-gray-700 mb-3">
                  <strong>You acknowledge:</strong>
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>AI-generated recipes are suggestions, not professional advice</li>
                  <li>You are responsible for verifying ingredient safety (especially allergens)</li>
                  <li>TalkToast is NOT liable for allergic reactions or foodborne illness</li>
                  <li>Always consult healthcare professionals for medical dietary needs</li>
                  <li>Nutritional information is approximate and may contain errors</li>
                </ul>
                <p className="text-base font-semibold text-red-800 mt-4">
                  Food Allergies: Always verify ingredients if you have food allergies. AI may make errors; double-check all recipes.
                </p>
              </div>
            </section>

            {/* Disclaimers and Warranties */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Disclaimers and Warranties</h2>
              <div className="bg-warm-cream rounded-lg p-6 border border-soft-tan">
                <p className="text-base text-gray-700 mb-3">
                  <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.</strong>
                </p>
                <p className="text-base text-gray-600 mb-3">
                  To the maximum extent permitted by law, TalkToast disclaims all warranties, including:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4">
                  <li>Implied warranties of merchantability and fitness for a particular purpose</li>
                  <li>Warranties regarding accuracy, reliability, or availability</li>
                  <li>Warranties that the Service will be uninterrupted or error-free</li>
                  <li>Warranties regarding AI-generated content accuracy</li>
                </ul>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Limitation of Liability</h2>
              <div className="bg-toast-gold/10 border border-toast-gold/30 rounded-lg p-6">
                <p className="text-lg font-semibold text-deep-charcoal mb-4">
                  Maximum Liability Cap
                </p>
                <p className="text-base text-gray-700 mb-3">
                  To the maximum extent permitted by law, TalkToast's total liability to you for any claims shall not exceed the <strong>greater of</strong>:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4 mb-4">
                  <li><strong>$100 USD</strong>, or</li>
                  <li><strong>The total amount you paid to TalkToast in the 12 months before the claim</strong></li>
                </ul>
                <p className="text-base text-gray-700 font-semibold">
                  BUT IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED $1,000 USD.
                </p>
                <p className="text-sm text-gray-600 mt-4">
                  This limitation does not apply to liability that cannot be excluded by law (fraud, gross negligence, personal injury).
                </p>
              </div>
            </section>

            {/* Dispute Resolution */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Dispute Resolution</h2>
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-6">
                <p className="text-base font-semibold text-red-800 mb-2">
                  PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.
                </p>
              </div>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3">Informal Resolution</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-4">
                Before filing a legal claim, you agree to contact us at <a href="mailto:legal@talktoast.com" className="text-toast-gold-700 hover:text-toast-gold-800">legal@talktoast.com</a> to attempt to resolve the dispute informally. We will work with you in good faith to resolve the issue within 30 days.
              </p>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3 mt-6">Binding Arbitration</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-4">
                If informal resolution fails, you agree that any dispute will be resolved through binding arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules.
              </p>
              <div className="bg-warm-cream rounded-lg p-6 border border-soft-tan mb-6">
                <p className="text-base text-gray-700 mb-3">
                  <strong>Arbitration Details:</strong>
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                  <li><strong>Location:</strong> Your county of residence (for claims under $10,000) or King County, WA (for larger claims)</li>
                  <li><strong>Fees:</strong> TalkToast pays all AAA fees for claims under $10,000</li>
                  <li><strong>Decision:</strong> Arbitrator's decision is final and binding</li>
                  <li><strong>Small Claims Exception:</strong> Either party may bring claims in small claims court</li>
                </ul>
              </div>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3">Class Action Waiver</h3>
              <div className="bg-toast-gold/10 border border-toast-gold/30 rounded-lg p-6">
                <p className="text-base text-gray-700 font-semibold mb-2">
                  YOU AND TALKTOAST AGREE THAT DISPUTES WILL BE RESOLVED ON AN INDIVIDUAL BASIS ONLY.
                </p>
                <p className="text-base text-gray-600">
                  You waive any right to participate in a class action lawsuit or class-wide arbitration.
                </p>
              </div>

              <h3 className="text-2xl font-semibold text-deep-charcoal mb-3 mt-6">Opt-Out Right</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                You have the right to opt out of this arbitration provision. To opt out, email <a href="mailto:legal@talktoast.com" className="text-toast-gold-700 hover:text-toast-gold-800">legal@talktoast.com</a> within 30 days of creating your account with subject line "Arbitration Opt-Out". Include your name, email, and statement: "I opt out of the arbitration provision."
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Governing Law</h2>
              <p className="text-base text-gray-600 leading-relaxed">
                These Terms and any disputes will be governed by the laws of the <strong>State of Washington</strong>, without regard to conflict of law principles. The Federal Arbitration Act governs the interpretation and enforcement of the arbitration provision.
              </p>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-3xl font-bold text-deep-charcoal mb-4">Changes to These Terms</h2>
              <p className="text-base text-gray-600 leading-relaxed mb-4">
                We may update these Terms from time to time. For material changes, we will:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-600 ml-4 mb-4">
                <li>Email notification to your registered email address</li>
                <li>In-app notification upon next login</li>
                <li>Provide 30 days' notice before changes take effect</li>
              </ul>
              <p className="text-base text-gray-600 leading-relaxed">
                Continued use after the effective date constitutes acceptance of the updated Terms.
              </p>
            </section>

            {/* Contact Us */}
            <section className="bg-gradient-to-br from-toast-gold/10 to-transparent rounded-lg p-6 border border-toast-gold/20">
              <h2 className="text-2xl font-bold text-deep-charcoal mb-4">Questions About These Terms?</h2>
              <p className="text-base text-gray-600 mb-4">
                If you have questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-2">
                <p className="text-base text-gray-700">
                  <strong>Legal Inquiries:</strong> <a href="mailto:legal@talktoast.com" className="text-toast-gold-700 hover:text-toast-gold-800 font-semibold">legal@talktoast.com</a>
                </p>
                <p className="text-base text-gray-700">
                  <strong>General Support:</strong> <a href="mailto:support@talktoast.com" className="text-toast-gold-700 hover:text-toast-gold-800">support@talktoast.com</a>
                </p>
                <p className="text-base text-gray-700">
                  <strong>Privacy Requests:</strong> <a href="mailto:privacy@talktoast.com" className="text-toast-gold-700 hover:text-toast-gold-800">privacy@talktoast.com</a>
                </p>
              </div>
            </section>

            {/* Footer */}
            <div className="text-center pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-4">
                <strong>Last Updated:</strong> January 20, 2025 • <strong>Effective Date:</strong> January 20, 2025
              </p>
              <p className="text-sm text-gray-500 mb-4">
                TalkToast LLC • Tacoma, Washington, United States
              </p>
              <p className="text-base text-gray-700 mb-4">
                <strong>By using TalkToast, you acknowledge that you have read and agree to these Terms.</strong>
              </p>
              <div className="mt-6">
                <Link
                  to="/privacy-policy"
                  className="text-toast-gold-700 hover:text-toast-gold-800 font-medium"
                >
                  View Privacy Policy →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
